/**
 * The Help guide's content (Settings > Help centre): short task-based topics, one per thing a parent
 * does, grouped by feature. Written to match the screens as they are (2 Nov 2026), in the admin
 * manual's style (docs/USER-MANUAL.md): plain British English, a few short steps, **bold** for the
 * names on screen, then what happens and any rule.
 *
 * A line is plain text, or `phone(...)` / `web(...)` when it only applies to the phone app or the
 * browser - the guide shows each person only the lines for what they are using.
 *
 * Change a screen, change its topic here in the same PR (help/__tests__ checks every topic has a
 * title and at least one step on each platform).
 */

export type HelpPlatform = 'phone' | 'web';

/** One line of a topic. `**Name**` marks a name on screen, shown bold. */
export type HelpLine = string | { text: string; on: HelpPlatform };

export const HELP_AREAS = [
  'Getting started',
  'Home',
  'Student profile',
  'Academics',
  'Attendance and leave',
  'Early pickup',
  'CXC / CSEC',
  'Fees and payments',
  'Wallet',
  'Canteen',
  'Store',
  'Messages and events',
  'Notifications and settings',
] as const;

export type HelpArea = (typeof HELP_AREAS)[number];

export interface HelpTopic {
  id: string;
  area: HelpArea;
  title: string;
  steps: HelpLine[];
  /** What happens, and any rule. */
  notes?: HelpLine[];
  /** Other words people search with ("sick" for leave). */
  keywords?: string[];
}

/** The school office, as on the old Help Center card. */
export const HELP_SUPPORT = {
  phone: '+1 (345) 640-2630',
  tel: 'tel:+13456402630',
  hours: ['Monday - Thursday: 8:30 AM - 5:00 PM', 'Friday: 8:30 AM - 1:00 PM'],
};

const phone = (text: string): HelpLine => ({ text, on: 'phone' });
const web = (text: string): HelpLine => ({ text, on: 'web' });

export const HELP_TOPICS: HelpTopic[] = [
  // ── Getting started ──────────────────────────────────────────────────────────────────────
  {
    id: 'sign-in',
    area: 'Getting started',
    title: 'Sign in',
    steps: [
      phone('Open the app and tap **Sign In**, then sign in with Microsoft using the email address the school invited.'),
      web('Click **Sign in with your email**, then sign in with Microsoft in the window that opens, using the email address the school invited.'),
      web('If the sign-in window does not open, allow pop-ups for this site and try again.'),
    ],
    notes: [
      'Only parents invited by the school can sign in. **Access Denied** means the email has not been invited, or access for this login is switched off - call the school office.',
      '**No Student Linked** means no child is linked to your login yet. The school office links them.',
      '**Sign in with a different account** signs you out so you can use another email address.',
    ],
    keywords: ['login', 'log in', 'password', 'microsoft', 'access denied', 'invite'],
  },
  {
    id: 'switch-child',
    area: 'Getting started',
    title: 'Switch between your children',
    steps: [
      phone("Tap your child's photo or name at the top of the screen."),
      phone('In **Switch child**, tap the child you want.'),
      web("Click your child's name at the top right and choose another child from the list."),
    ],
    notes: [
      'Everything in the app is for the child chosen at the top: attendance, homework, fees, the wallet, canteen orders and the rest. Choose the child first, then open the screen.',
      'You can switch only when more than one child is linked to your login. The app remembers your choice.',
    ],
    keywords: ['sibling', 'another child', 'second child', 'children', 'kids', 'change child'],
  },
  {
    id: 'find-your-way',
    area: 'Getting started',
    title: 'Find your way around',
    steps: [
      phone('The tabs at the bottom are **Home**, **Academics**, **Fees**, **Store** and **Inbox**.'),
      phone('**Attendance**, **Leave**, **Early Pickup**, **Wallet**, **Canteen** and **Contact Admin** open from their cards on **Home**.'),
      phone('The bell at the top opens your notifications. The sun icon beside it opens **Settings**.'),
      web('Use the menu on the left: **Dashboard**, **Student profile**, **Academics**, **Attendance**, **Leaves**, **Fees & payments**, **Store**, **Wallet**, **Canteen**, **Communication** and **Settings**.'),
      web('In a narrow window the menu is hidden: the menu icon at the top left opens it.'),
      web('The bell at the top opens your notifications.'),
    ],
    keywords: ['menu', 'navigation', 'tabs', 'where'],
  },
  {
    id: 'sign-out',
    area: 'Getting started',
    title: 'Sign out',
    steps: [
      'Open **Settings** and tap **Log out**.',
      web('On a wide screen, the red sign-out icon at the top right does the same.'),
    ],
    notes: [phone('Signing out also stops notifications to this phone.')],
    keywords: ['log out', 'logout', 'sign off'],
  },
  {
    id: 'get-help',
    area: 'Getting started',
    title: 'Get help from a person',
    steps: [
      'Call the school office on the number at the top of this guide, during the hours shown.',
      'Or write to them: tap **Contact Admin** on **Home**.',
    ],
    keywords: ['phone', 'call', 'support', 'contact', 'office', 'number'],
  },

  // ── Home ─────────────────────────────────────────────────────────────────────────────────
  {
    id: 'home',
    area: 'Home',
    title: 'Use the Home screen',
    steps: [
      phone('Tap **Home** at the bottom.'),
      web('Choose **Dashboard** in the menu.'),
      "**Student details** shows your child's name, grade, ID and academic year. **View profile** opens the full profile.",
      "**Performance summary** shows this month's attendance and the last exam result.",
      'The cards open each feature: **Pay Fees**, **Attendance**, **Leave**, **Results**, **Early Pickup**, **Wallet**, **Canteen** and **Contact Admin**.',
      '**Recent alerts** lists the latest notifications and upcoming exams. **See all alerts** opens the rest.',
    ],
    notes: [
      'When money is owed, the **Fees Amount Pending** banner shows the total for all your children. **Pay now** opens **Pay & top up** in the wallet.',
      "During a lesson, **Class in session** shows what your child has now. **Timetable** opens the day's timetable.",
    ],
    keywords: ['dashboard', 'start', 'overview'],
  },
  {
    id: 'fee-plan',
    area: 'Home',
    title: 'Choose a fee instalment plan',
    steps: [
      'When **Welcome to DigitalCampus!** appears on Home, tap **Select Installment Plan**.',
      'Choose **Annual Plan**, **Quarterly** or **Monthly**.',
      "Under **Sibling & Multi-Child Discount**, say whether you have other children at the school. If you do, choose how many and type each child's full name, then pick the match when it appears.",
      'Tap **Save & Continue**.',
    ],
    notes: [
      'This appears until a plan is chosen for the child at the top.',
      'The school office checks a sibling discount and adjusts your invoices.',
      '**Fee Structure Not Found** means the school has not set fees for the class yet - call the school office.',
    ],
    keywords: ['installment', 'instalment', 'annual', 'quarterly', 'monthly', 'sibling', 'discount', 'onboarding'],
  },
  {
    id: 'complete-profile',
    area: 'Home',
    title: "Complete your child's profile",
    steps: [
      'After the fee plan, tap **Complete Profile**.',
      'Fill in the fields marked * - both parents and an emergency contact among them - and tap **Save**.',
    ],
    notes: ['This comes back on Home until the profile has an emergency contact.'],
    keywords: ['onboarding', 'first time', 'required'],
  },

  // ── Student profile ──────────────────────────────────────────────────────────────────────
  {
    id: 'profile',
    area: 'Student profile',
    title: "See your child's profile",
    steps: [
      phone('On **Home**, tap **View profile**.'),
      web('Choose **Student profile** in the menu.'),
      'Scroll through **Personal details**, **Contact Information**, **Enrollment Info**, **Family & Contacts**, **Medical Information**, **Additional Information** and **Documents**.',
      '**Quick links** at the bottom open **Attendance**, **Leaves**, **Academics** and **Fees**.',
    ],
    notes: ['**Enrollment Info** is kept by the school and cannot be changed here.'],
    keywords: ['details', 'student', 'record', 'information'],
  },
  {
    id: 'profile-photo',
    area: 'Student profile',
    title: "Change your child's photo",
    steps: [
      'Open the profile and tap the camera badge on the photo.',
      'Choose a photo.',
      web('The camera badge on the **Dashboard** also lets you drag a photo in, or take one with **Use Camera**.'),
    ],
    notes: ['JPG or PNG only. If the photo is too large, the app says the size limit.'],
    keywords: ['picture', 'image', 'camera'],
  },
  {
    id: 'profile-details',
    area: 'Student profile',
    title: 'Change personal or contact details',
    steps: [
      'Open the profile and tap the pencil beside **Personal details** or **Contact Information**.',
      'Change what you need and tap **Save**.',
    ],
    notes: [
      "Changes go straight onto the school's record - there is no approval step.",
      'An email address or phone number already used by another pupil is refused.',
    ],
    keywords: ['address', 'email', 'phone number', 'edit', 'update', 'nationality', 'birthday'],
  },
  {
    id: 'emergency-contact',
    area: 'Student profile',
    title: 'Choose the emergency contact',
    steps: [
      'Open the profile and tap the pencil beside **Family & Contacts**.',
      'Choose the **Contact Person** and check the **Emergency Phone Number**.',
      'Tap **Save**.',
    ],
    notes: ['Only family members already on the record can be chosen. To add someone new, edit the full profile.'],
    keywords: ['emergency', 'family', 'relative', 'guardian'],
  },
  {
    id: 'profile-full',
    area: 'Student profile',
    title: 'Edit the full profile',
    steps: [
      phone('Open the profile and tap the pencil at the top right.'),
      web("Open the profile and click the pencil on the blue card with your child's name."),
      'Tap a section heading to open or close it, and fill in the fields marked *.',
      'To make the mother, father or guardian the emergency or financial contact, tick **Is Emergency Contact** or **Is Financial Contract** on their section.',
      'Tap **Save**.',
    ],
    notes: ['Only one person can be the emergency contact, and one the financial contact.'],
    keywords: ['mother', 'father', 'guardian', 'parents', 'visa', 'previous school', 'financial'],
  },
  {
    id: 'medical',
    area: 'Student profile',
    title: 'Update medical or other information',
    steps: [
      'Open the profile and tap the pencil beside **Medical Information** or **Additional Information**.',
      'Change the answers. **Yes** opens a box for the details.',
      'Tap **Save**.',
    ],
    notes: ['Only questions already answered show here. To answer the others, edit the full profile.'],
    keywords: ['medical', 'health', 'allergy', 'medication', 'condition'],
  },
  {
    id: 'documents',
    area: 'Student profile',
    title: 'Upload a document',
    steps: [
      phone('Open the profile and tap the upload arrow beside **Documents**.'),
      phone('Choose the **Document Type**, then the file, and tap **Upload**.'),
      web('Open the profile. Each document the school collects has its own tile: **Upload** adds it. Once uploaded, **Preview**, **Download** and **Replace** appear.'),
    ],
    notes: [
      'Each type says which files it takes and the largest size.',
      phone('A document cannot be replaced on the phone once uploaded. Replace it from a browser.'),
    ],
    keywords: ['file', 'certificate', 'passport', 'birth certificate', 'upload'],
  },
  {
    id: 'id-card',
    area: 'Student profile',
    title: 'Show or download the ID card',
    steps: [
      'Open the profile and tap **View digital ID card**.',
      'Show the card on screen, or tap **Download** to save it as a PDF.',
    ],
    notes: ['The card has the student ID as a barcode, the emergency contact and the home address. To change these, edit the profile.'],
    keywords: ['student id', 'barcode', 'card', 'pdf'],
  },

  // ── Academics ────────────────────────────────────────────────────────────────────────────
  {
    id: 'timetable',
    area: 'Academics',
    title: 'See the timetable',
    steps: [
      phone('Tap **Academics**, then **Timetable**.'),
      web('Choose **Academics** in the menu, then **Timetable**.'),
      'Tap a day, **Mon** to **Sat**, to see its lessons, times, teachers and breaks.',
    ],
    notes: ['It opens on today.'],
    keywords: ['lessons', 'classes', 'schedule', 'periods', 'subjects'],
  },
  {
    id: 'homework',
    area: 'Academics',
    title: 'See homework and hand it in',
    steps: [
      'In **Academics**, open **Homework**. It lists homework from the last 30 days and the next 30.',
      "Tap a piece of homework to read it and open the teacher's files.",
      'To hand it in, add a note if you like, tap **Attach files** (up to 10 files, 10 MB each), then tap **Hand in**.',
      '**Hand in again** sends a new version. New files replace the ones sent before.',
    ],
    notes: [
      'Each piece shows **Pending**, **Submitted** or **Graded**.',
      'Papers sat in class say **Not collected online**: there is nothing to upload.',
      "Marks and the teacher's feedback appear once the teacher publishes them.",
    ],
    keywords: ['submit', 'upload', 'assignment', 'due', 'work'],
  },
  {
    id: 'projects',
    area: 'Academics',
    title: 'Hand in a project',
    steps: [
      'In **Academics**, open **Projects** and tap a project.',
      'Under **Your submission**, write your **Notes**.',
      web('Click **Attach a file** to add one file (up to 15 MB).'),
      'Tap **Submit Project**.',
    ],
    notes: [
      'A project is handed in once and cannot be changed afterwards.',
      phone('Files can only be attached in a browser. On the phone, a note is needed.'),
      'After the deadline the project may say its submission window has closed. Marks appear once it is **Graded**.',
    ],
    keywords: ['submit', 'project', 'upload'],
  },
  {
    id: 'assessments',
    area: 'Academics',
    title: 'See tests and marks',
    steps: [
      'In **Academics**, open **Assessments**.',
      '**Assigned** lists the tests, quizzes and exams set for the weeks around today. Tap one for its details.',
      "Below are the published results: score, percentage and grade, with the class average, rank and the teacher's feedback where the school shows them.",
      '**Learning progress** shows how your child is doing on each learning outcome.',
    ],
    notes: ['Marks appear only once the teacher publishes them.'],
    keywords: ['test', 'quiz', 'marks', 'grades', 'results', 'score'],
  },
  {
    id: 'exams',
    area: 'Academics',
    title: 'See exam results and the exam timetable',
    steps: [
      phone('Tap **Academics**, then **Exams** - or tap **Results** on Home.'),
      web('Choose **Academics**, then **Exams** - or click **Results** on the Dashboard.'),
      'The banner at the top shows the next exam. Tap it for the exam timetable.',
      'To see another exam, tap the exam name, choose the **Academic Year** and **Exam Type**, then tap **Apply**.',
      "The table lists each subject's marks and grade.",
    ],
    notes: ['Only exams with published results can be chosen.'],
    keywords: ['exam', 'results', 'marks', 'grades', 'schedule', 'timetable'],
  },
  {
    id: 'report-cards',
    area: 'Academics',
    title: 'Download a report card',
    steps: [
      'In **Academics**, open **Exams** and scroll to **Report cards**.',
      'Tap **Download PDF** on a report card, or tap the card to see it first.',
      'For an earlier year, choose the year from the exam name at the top.',
    ],
    notes: [
      'Only report cards the school has published appear.',
      phone('On Android the PDF goes to Downloads. On an iPhone, choose where to save or share it.'),
    ],
    keywords: ['report', 'term report', 'pdf', 'grades'],
  },

  // ── Attendance and leave ─────────────────────────────────────────────────────────────────
  {
    id: 'attendance',
    area: 'Attendance and leave',
    title: 'Check attendance',
    steps: [
      phone('On **Home**, tap **Attendance**.'),
      web('Choose **Attendance** in the menu.'),
      "**Daily** shows today's registers and recent days. **Weekly** shows this week, and **Monthly** a calendar: tap a day to see its registers.",
    ],
    notes: [
      'Blue is present, amber leave and red absent. Late, half day and remote show when the month has them.',
      'Approved leave does not count against the attendance percentage. Below 80%, a warning shows.',
      '**By subject** shows attendance in each subject this year.',
      "The chip at the top of the app shows today's status, such as present, absent or on leave.",
    ],
    keywords: ['absent', 'present', 'late', 'register', 'percentage'],
  },
  {
    id: 'leave',
    area: 'Attendance and leave',
    title: 'Tell the school your child will be away',
    steps: [
      phone('On **Home**, tap **Leave**, then **Apply for leave**.'),
      phone('Choose the **Start Date** and **End Date**, write the **Reason** (up to 250 characters) and tap **Submit Application**.'),
      web('Choose **Leaves** in the menu, then **Apply for leave**.'),
      web('Fill in **From Date**, **To Date** and **Reason**, then click **Submit**.'),
    ],
    notes: [
      'Dates start from today. The leave is recorded straight away on each school day in the range - weekends and holidays are skipped.',
      'Dates that overlap leave already given are refused.',
      'Leave cannot be changed or cancelled in the app - call the school office.',
    ],
    keywords: ['absence', 'absent', 'sick', 'ill', 'holiday', 'away', 'travel', 'leave request'],
  },

  // ── Early pickup ─────────────────────────────────────────────────────────────────────────
  {
    id: 'early-pickup',
    area: 'Early pickup',
    title: 'Ask to collect your child early',
    steps: [
      'On **Home**, tap **Early Pickup**.',
      phone('Open **Request Pickup**.'),
      web('Click **New request**.'),
      'Choose the **Pickup date**, **Pickup time**, a **Reason** and the **Pickup person**. Add **Notes for the school** if you like.',
      'Tap **Send Request**.',
    ],
    notes: [
      'The pickup person must be one of the contacts the school has on record. If there are none, ask the school office to add one first.',
      'One open request per child per day. A date or time already past is refused.',
      'The school approves or rejects it, and you get a notification either way.',
    ],
    keywords: ['collect', 'pick up', 'leave early', 'appointment', 'release'],
  },
  {
    id: 'early-pickup-track',
    area: 'Early pickup',
    title: 'Track or cancel an early pickup',
    steps: [
      phone('On the Early Pickup screen, open **My Requests**.'),
      web('The Early Pickup page lists your requests.'),
      'Filter with **Pending**, **Approved**, **Checked out**, **Rejected** or **Cancelled**.',
      'To cancel, tap **Cancel request**, add a reason if you like, and tap **Cancel request** again to confirm.',
    ],
    notes: [
      '**Pending** is waiting for the school. **Approved** means your child is waiting at school - come to the school office. **Checked out** means your child has been collected.',
      'A request can be cancelled while it is pending or approved, not once your child has been collected.',
    ],
    keywords: ['status', 'cancel', 'pickup'],
  },

  // ── CXC / CSEC ───────────────────────────────────────────────────────────────────────────
  {
    id: 'cxc',
    area: 'CXC / CSEC',
    title: 'Register for CXC / CSEC exams',
    steps: [
      'While registration is open, Home shows the **CXC / CSEC registration** banner for pupils above Year 6. Tap **Register now**.',
      'Choose the **Examination sitting**.',
      'Check the candidate and contact details, then add a photo and an identification document (image or PDF, up to 5 MB).',
      'Tick the subjects, and **Re-sit** where it applies. Check the **Fee structure** estimate.',
      'Sign as the candidate, and as parent or guardian if you wish.',
      phone('Tap **Submit registration**.'),
      web('Click **Submit CXC Registration**, check the details, then **Confirm & Submit Application**. You can print the form there.'),
    ],
    notes: [
      'You get an invoice number. Bring it, proof of ID and the payment to the school office: the registration is not final with CXC until it is paid.',
      phone('Earlier registrations and their status are under **Previous**.'),
      web('Earlier registrations and their status are under **Previous registrations**.'),
    ],
    keywords: ['cxc', 'csec', 'cape', 'exam registration', 'external exams', 'subjects'],
  },

  // ── Fees and payments ────────────────────────────────────────────────────────────────────
  {
    id: 'fees',
    area: 'Fees and payments',
    title: 'See what you owe',
    steps: [
      phone('Tap **Fees** at the bottom.'),
      web('Choose **Fees & payments** in the menu.'),
      phone('The top card shows the total outstanding and the next due date. **Pending installments** lists what is still to pay: tap one for its breakdown.'),
      phone('Tap **Installments** to see every instalment and its status.'),
      web('The cards show the total fee, what is paid, and what is due now, overdue and upcoming. **Installment Schedule** lists each instalment with its status.'),
    ],
    notes: ['**Overdue** is past its due date. **Due** falls within 30 days, **Upcoming** later.'],
    keywords: ['fees', 'invoice', 'balance', 'owe', 'tuition', 'instalment', 'installment', 'overdue'],
  },
  {
    id: 'pay-fees',
    area: 'Fees and payments',
    title: 'Pay fees',
    steps: [
      'When the school takes online payments: open **Wallet**, then **Pay & top up**.',
      'Tick the fees to pay - overdue fees and those due within 31 days are ticked for you - and tap **Pay**.',
      'Pay in the window that opens. Each fee is marked paid as soon as the payment is confirmed.',
      phone('Otherwise pay at the school office. On **Fees**, **Pay now** lists the invoices and how to pay at school.'),
      web('Otherwise pay at the school office. On **Fees & payments**, **Pay Due Amount** lists the invoices and how to pay at school.'),
    ],
    notes: [
      'Card, UPI or net banking, where the school offers them.',
      phone('Payments made at the school counter show under **Office receipts** on **Fees**.'),
    ],
    keywords: ['pay', 'payment', 'card', 'invoice', 'online', 'tuition'],
  },
  {
    id: 'fee-receipt',
    area: 'Fees and payments',
    title: 'Get a fee receipt',
    steps: [
      phone('On **Fees**, under **Payment history**, tap **Receipt** - or tap **View receipt** in **Installments**.'),
      phone('On Android the PDF is saved to Downloads. On an iPhone, choose where to save or share it.'),
      phone('**Office receipts** lists payments made at the school counter for all your children, each with **Receipt**.'),
      web('Under **Payments & Receipts**, click the printer icon to print or save the receipt.'),
    ],
    keywords: ['receipt', 'proof', 'pdf', 'print'],
  },

  // ── Wallet ───────────────────────────────────────────────────────────────────────────────
  {
    id: 'wallet',
    area: 'Wallet',
    title: 'Check the wallet and statement',
    steps: [
      phone('On **Home**, tap **Wallet**.'),
      web('Choose **Wallet** in the menu.'),
      "With more than one child, tap a child's name to see their wallet.",
      'The card shows the balance and the Payer ID. Tap any line of the statement for its details.',
    ],
    notes: [
      'Unpaid bills are paid first from the next top-up, or at the school office.',
      'A frozen or closed wallet cannot be spent from until the school office sorts it out.',
    ],
    keywords: ['balance', 'statement', 'transactions', 'payer id', 'money'],
  },
  {
    id: 'wallet-top-up',
    area: 'Wallet',
    title: 'Top up the wallet',
    steps: [
      'In **Wallet**, tap **Top up** on the balance card.',
      'Tap a quick amount or type one, then tap **Pay** and pay in the window that opens.',
      'The money shows in the wallet once the payment is confirmed.',
    ],
    notes: [
      'The school sets the smallest top-up and the most a wallet can hold. The form shows both.',
      'If the school does not take online payments, pay at the school office with cash or card, quoting the Payer ID. It shows in the wallet straight away.',
    ],
    keywords: ['top up', 'add money', 'load', 'credit', 'payer id'],
  },
  {
    id: 'pay-and-top-up',
    area: 'Wallet',
    title: 'Pay fees and top up in one go',
    steps: [
      'In **Wallet**, open **Pay & top up**. It covers all your children.',
      'Tick the fees and wallet bills to pay.',
      "To add money as well, choose an amount under each child's wallet.",
      'Check **This payment**, then tap **Pay**.',
    ],
    notes: [
      'Each fee and bill is marked paid, and each wallet topped up, as soon as the payment is confirmed - even if you close the screen.',
      '**How it was split** shows where the money went.',
      'Without online payments, the screen tells you to bring the list to the school office and quote the Payer IDs.',
    ],
    keywords: ['pay', 'top up', 'fees', 'bills', 'all children'],
  },
  {
    id: 'block-card',
    area: 'Wallet',
    title: 'Block a lost card',
    steps: [
      'In **Wallet**, find the card under **Cards** and tap **Block**.',
      'Confirm with **Block card**.',
    ],
    notes: ['The card stops working at once; the balance is not touched. The school office issues a new card - a blocked card cannot be unblocked in the app.'],
    keywords: ['lost', 'stolen', 'card', 'block', 'freeze'],
  },

  // ── Canteen ──────────────────────────────────────────────────────────────────────────────
  {
    id: 'canteen-order-day',
    area: 'Canteen',
    title: 'Order meals for a day',
    steps: [
      'The canteen always orders for the child chosen at the top of the app, so choose your child there first.',
      phone('On **Home**, tap **Canteen** (or **Order food** on the wallet card).'),
      web('Choose **Canteen** in the menu.'),
      'Open **Order meals**, then **Order by day**. Tap the day - today is marked **Today** - or tap **Order for today**.',
      'Tap a dish for each meal; tap it again to remove it. Add drinks and snacks under **Extras** with +.',
      "Tap **Review order** (**Review today's order** for today), check it, then tap **Confirm order for** your child.",
    ],
    notes: [
      "Meals are paid from your child's wallet. Nothing is charged until you confirm, and **Order placed** shows what was ordered and the wallet after.",
      'If the wallet is short, the bar says how much to add. **Top up** opens **Pay & top up**.',
    ],
    keywords: ['lunch', 'food', 'meal', 'dinner', 'cafeteria', 'order', 'today'],
  },
  {
    id: 'canteen-plan-week',
    area: 'Canteen',
    title: 'Plan the week',
    steps: [
      'In **Canteen**, open **Order meals**, then **Plan the week**. Choose the week at the top.',
      'Pick a dish for each meal on each day.',
      '**Same lunch every day** copies the chosen lunch to the other days. **Copy last week** copies the last week you ordered.',
      'Tap **Review weekly order**, check the days, then tap **Confirm order for** your child.',
    ],
    notes: ['If a copy would change meals you have already chosen, the app asks first: **Replace** or **Keep mine**.'],
    keywords: ['week', 'weekly', 'copy', 'same lunch', 'plan ahead'],
  },
  {
    id: 'canteen-menu',
    area: 'Canteen',
    title: 'See the canteen menu',
    steps: [
      'In **Canteen**, open **Menu** and choose the week at the top.',
      "Each day lists its meals with your child's price, **Veg** and **Vegan** dishes, and what each dish contains.",
      'Tap **Order this day** (or **Order for today**) to go straight to ordering that day.',
    ],
    notes: [
      'The prices shown are the ones your child pays.',
      'Extras - drinks and snacks - are the same every school day, up to 5 of one item a day.',
    ],
    keywords: ['menu', 'dishes', 'prices', 'vegetarian', 'vegan', 'allergens'],
  },
  {
    id: 'canteen-delivery',
    area: 'Canteen',
    title: 'Where your child gets the meal',
    steps: [
      'The school sets, for each class, whether meals are delivered to the classroom or collected at the cafeteria.',
      'Next to the prices at the top of **Canteen** and on the **Menu**, it says "Delivered to the classroom" or "Collected at the cafeteria".',
    ],
    keywords: ['delivered', 'classroom', 'collected', 'cafeteria', 'collect'],
  },
  {
    id: 'canteen-cut-offs',
    area: 'Canteen',
    title: 'Canteen cut-offs',
    steps: [
      'Each meal shows its cut-off, for example **Order by** 8:00 pm Mon 2 Nov. After that it shows **Closed** and cannot be ordered.',
      'Extras have their own cut-off for the day.',
      'The school may also close orders for the same day at a set time. The Canteen screen then says "Orders for the same day close at ... at the latest."',
    ],
    notes: [
      'After the cut-off, a meal cannot be ordered, changed or cancelled in the app - call the school office.',
      '**Canteen ordering is switched off at the moment** means the school has paused ordering.',
    ],
    keywords: ['deadline', 'cut off', 'cutoff', 'too late', 'time', 'closed', 'same day'],
  },
  {
    id: 'canteen-change-dish',
    area: 'Canteen',
    title: 'Change a dish',
    steps: [
      'In **Order meals**, find a meal you ordered for tomorrow or later and tap **Change dish**.',
      'Choose the other dish. The app shows whether more comes from the wallet or money goes back.',
      'Tap **Change dish** to confirm, or **Keep it** to leave it as it is.',
    ],
    notes: [
      "Only meals from tomorrow on can be changed, and only before their cut-off. Today's meals cannot be changed - cancel instead while the cut-off allows.",
      "What you paid goes back to the wallet and the new dish is charged at today's price.",
    ],
    keywords: ['change', 'swap', 'different dish', 'edit order'],
  },
  {
    id: 'canteen-cancel',
    area: 'Canteen',
    title: 'Cancel a meal',
    steps: [
      'In **Order meals**, find the meal or extras you ordered and tap **Cancel**.',
      "Confirm with **Cancel order**. The money goes back to your child's wallet.",
    ],
    notes: ['**Cancel** shows only until the cut-off.'],
    keywords: ['cancel', 'refund', 'remove order'],
  },
  {
    id: 'canteen-allergies',
    area: 'Canteen',
    title: 'Allergy notes on dishes',
    steps: [
      'A red **Allergy** tag means the school nurse has recorded that allergy for your child. The dish cannot be chosen.',
      'An amber note means the dish may contain something your family wrote down at registration. It is a warning only: the dish can still be chosen.',
      'Ask the school nurse to confirm an allergy. Once confirmed, dishes with it are blocked.',
    ],
    notes: ['Under the meals, the Canteen screen lists the allergies it checks for your child.'],
    keywords: ['allergy', 'allergies', 'nurse', 'nuts', 'gluten', 'dairy', 'red', 'amber', 'warning'],
  },
  {
    // Same words as the Student app branch (PR 2935) put in the old Help Center card, so the two agree.
    id: 'canteen-child-orders',
    area: 'Canteen',
    title: 'Can my child order for themselves?',
    steps: [
      "If the school allows it for their class, your child can order and cancel their own meals in the Student app, paid from the same wallet. Those meals show 'Ordered by' and your child's name here, and you can still change or cancel them before the cut-off.",
    ],
    keywords: ['student app', 'pupil', 'own meals', 'self', 'ordered by'],
  },
  {
    id: 'canteen-history',
    area: 'Canteen',
    title: 'See past canteen orders',
    steps: [
      'In **Canteen**, open **Order history** and choose the month.',
      'The top shows the orders, what was spent and how many were cancelled. Upcoming orders come first, then each day.',
      'Tap an order to see every item in it.',
    ],
    notes: ['An order placed or changed by someone else says who, for example "Ordered by the class teacher".'],
    keywords: ['history', 'past orders', 'spent', 'previous'],
  },

  // ── Store ────────────────────────────────────────────────────────────────────────────────
  {
    id: 'store',
    area: 'Store',
    title: 'Order from the school store',
    steps: [
      phone('Tap **Store** at the bottom.'),
      web('Choose **Store** in the menu.'),
      'Pick a category, then tap **Add** on an item, choosing a size if asked.',
      'Under **Your order**, change quantities with - and +, and add a note for the office if you like.',
      'Tap **Reserve for 48 h · pay at school**.',
    ],
    notes: [
      'Items are held for 48 hours. Pay at the school office within that time, or the order expires and the items go back on sale.',
      'The order is for the child chosen at the top. Store orders are paid at the school office, not in the app.',
    ],
    keywords: ['uniform', 'stationery', 'shop', 'buy', 'books', 'supplies'],
  },
  {
    id: 'store-orders',
    area: 'Store',
    title: 'Track or cancel a store order',
    steps: [
      'In **Store**, **My orders** lists your orders with their status.',
      'While an order shows **Awaiting payment**, **Cancel** cancels it.',
    ],
    notes: ['**Ready for pickup** means it is paid: collect it from the school office. **Expired** means it was not paid within 48 hours.'],
    keywords: ['order status', 'collect', 'cancel'],
  },

  // ── Messages and events ──────────────────────────────────────────────────────────────────
  {
    id: 'chat',
    area: 'Messages and events',
    title: 'Message a teacher',
    steps: [
      phone('Tap **Inbox**, then **Chat**.'),
      web('Choose **Communication**, then **Chat**.'),
      "It opens on your child's class teacher. To write to another of your child's teachers, tap their name; a star marks the class teacher.",
      'Type your message and tap send.',
    ],
    notes: ['Chats are one-to-one with each teacher, text only. A red number shows unread messages.'],
    keywords: ['chat', 'teacher', 'message', 'contact teacher', 'talk'],
  },
  {
    id: 'concern',
    area: 'Messages and events',
    title: 'Raise a concern',
    steps: [
      phone('Tap **Inbox**, then **Complaints**, then **Raise a concern or suggestion**.'),
      web('Choose **Communication**, then **Complaints**, and click **Raise a concern or suggestion**.'),
      'Describe the concern or suggestion and tap **Submit**.',
    ],
    notes: ['It is raised for the child chosen at the top.'],
    keywords: ['complaint', 'concern', 'suggestion', 'feedback', 'problem'],
  },
  {
    id: 'concern-follow',
    area: 'Messages and events',
    title: 'Reply to or close a concern',
    steps: [
      "In **Complaints**, tap the concern to read the school's replies.",
      'Write in the reply box and tap **Send reply**.',
      'When it is sorted, tap **Mark as resolved**, with a closing note if you like.',
    ],
    notes: ['It closes only when both you and the school have confirmed. **Withdraw my confirmation** takes yours back.'],
    keywords: ['reply', 'resolve', 'close', 'complaint'],
  },
  {
    id: 'contact-admin',
    area: 'Messages and events',
    title: 'Message the school office',
    steps: [
      'On **Home**, tap **Contact Admin**.',
      'Write the **Subject** and **Message**, then tap **Send message**.',
    ],
    notes: ["It goes to the school office by email, with your name and your child's details. The office replies by email."],
    keywords: ['office', 'admin', 'email', 'contact', 'question'],
  },
  {
    id: 'events',
    area: 'Messages and events',
    title: 'See events and photos',
    steps: [
      phone('Tap **Inbox**, then **Events** or **Gallery**.'),
      web('Choose **Communication**, then **Events** or **Gallery**.'),
      'Tap an event to see its date, time, place and details.',
      "**Gallery** shows your child's class photos and achievements.",
    ],
    notes: [web('Click a photo to see it full size.')],
    keywords: ['events', 'calendar', 'photos', 'gallery', 'achievements'],
  },

  // ── Notifications and settings ───────────────────────────────────────────────────────────
  {
    id: 'notifications',
    area: 'Notifications and settings',
    title: 'Read notifications',
    steps: [
      'Tap the bell at the top.',
      'Tap a notification to mark it read. Attendance, homework and payment notifications open their page.',
      'Tap **Acknowledge** when the school asks you to confirm you have seen it. **Reply to teacher** opens the chat.',
    ],
    notes: [
      'Read notifications leave the list, unless they still need acknowledging. Emergency alerts show in red at the top.',
      phone('The **Alerts** tab in **Inbox** shows the same list.'),
      web('The **Alerts** tab in **Communication** shows the same list.'),
    ],
    keywords: ['alerts', 'bell', 'messages', 'emergency', 'acknowledge'],
  },
  {
    id: 'notification-settings',
    area: 'Notifications and settings',
    title: 'Choose how notifications reach you',
    steps: [
      'Open **Settings** and find the notification preferences.',
      'For each kind of message, tap the channels you want: **Email**, **WhatsApp**, **Push**, **In-app** and **SMS**.',
      'Tap **Save preferences**.',
    ],
    notes: [
      'Emergency alerts always arrive on every channel. A padlock marks a channel the school has made mandatory.',
      'The choice is for your login, for all your children.',
    ],
    keywords: ['email', 'sms', 'whatsapp', 'push', 'notifications', 'channels', 'turn off'],
  },
  {
    id: 'settings',
    area: 'Notifications and settings',
    title: 'Change app settings',
    steps: [
      phone('Tap the sun icon at the top to open **Settings**.'),
      web('Choose **Settings** in the menu.'),
      'Turn **Dark mode** on or off.',
      'Choose a **Language** and **Region**.',
    ],
    notes: ['These are saved on this phone or browser only. The app itself is in English for now.'],
    keywords: ['dark mode', 'theme', 'language', 'region', 'preferences'],
  },
];
